import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowUpRight, SendHorizontal, ShieldCheck } from "lucide-react";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";
import { fetchTests, selectTests } from "../../ABTest/store/abtestSlice";
import { fetchAllMetricsapi, selectTestMetrics } from "../../ABTest/store/metricsSlice";

const DAYS_TO_SHOW = 6;

function formatDateLabel(date) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function testId(test) {
  return test?._id || test?.metadata?.testId || "";
}

function testName(test) {
  return test?.basicInfo?.testName || "Untitled test";
}

function conversionEvent(metrics) {
  return metrics.some((metric) => metric.event === "purchase") ? "purchase" : "button_click";
}

function summarizeMetrics(metrics = []) {
  const event = conversionEvent(metrics);
  const summary = {
    control: { views: 0, conversions: 0 },
    variant: { views: 0, conversions: 0 },
  };

  metrics.forEach((metric) => {
    if (!summary[metric.group]) return;
    if (metric.event === "view") summary[metric.group].views += 1;
    if (metric.event === event) summary[metric.group].conversions += 1;
  });

  Object.values(summary).forEach((group) => {
    group.rate = group.views ? (group.conversions / group.views) * 100 : 0;
  });

  const lift = summary.control.rate
    ? ((summary.variant.rate - summary.control.rate) / summary.control.rate) * 100
    : null;

  return { ...summary, lift, conversionLabel: event === "purchase" ? "purchase" : "click" };
}

function buildDailySeries(metrics = []) {
  const event = conversionEvent(metrics);
  const days = Array.from({ length: DAYS_TO_SHOW }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (DAYS_TO_SHOW - 1 - index));
    return {
      key: date.toISOString().slice(0, 10),
      label: formatDateLabel(date),
      control: { views: 0, conversions: 0 },
      variant: { views: 0, conversions: 0 },
    };
  });
  const byDate = new Map(days.map((day) => [day.key, day]));

  metrics.forEach((metric) => {
    const date = new Date(metric.timestamp);
    if (Number.isNaN(date.getTime())) return;
    const day = byDate.get(date.toISOString().slice(0, 10));
    if (!day || !day[metric.group]) return;
    if (metric.event === "view") day[metric.group].views += 1;
    if (metric.event === event) day[metric.group].conversions += 1;
  });

  return days.map((day) => ({
    label: day.label,
    control: day.control.views ? (day.control.conversions / day.control.views) * 100 : 0,
    variant: day.variant.views ? (day.variant.conversions / day.variant.views) * 100 : 0,
  }));
}

function chartPoints(series, key, width = 620, height = 138) {
  const max = Math.max(1, ...series.flatMap((day) => [day.control, day.variant]));
  return series.map((day, index) => {
    const x = 12 + index * ((width - 24) / Math.max(series.length - 1, 1));
    const y = height - 14 - (day[key] / max) * (height - 34);
    return `${x},${y}`;
  }).join(" ");
}

function statusPresentation(test, metrics) {
  const status = String(test?.metadata?.status || "draft").toLowerCase();
  const summary = summarizeMetrics(metrics);
  if (summary.lift !== null && (status === "active" || status === "completed" || status === "stopped")) {
    const winning = summary.lift >= 0 ? "Variant" : "Control";
    return {
      tone: summary.lift >= 0 ? "winning" : "watch",
      text: `${winning} leading ${Math.abs(summary.lift).toFixed(1)}%`,
    };
  }
  if (status === "active") return { tone: "active", text: "Running, collecting evidence" };
  if (status === "completed" || status === "stopped") return { tone: "complete", text: "Completed, ready for review" };
  if (status === "paused") return { tone: "paused", text: "Paused" };
  return { tone: "draft", text: "Draft" };
}

function InsightsCommand({ onSubmit }) {
  const [message, setMessage] = useState("");

  function submit(event) {
    event.preventDefault();
    const nextMessage = message.trim();
    if (!nextMessage) return;
    onSubmit(nextMessage);
    setMessage("");
  }

  return (
    <form className="cf-home-command" onSubmit={submit}>
      <textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit(event);
          }
        }}
        rows={1}
        placeholder="Ask AI Insights about your landing pages or experiments..."
        aria-label="Ask AI Insights about landing page experiments"
      />
      <div className="cf-home-command-footer">
        <span><ShieldCheck size={14} /> Continues in your existing AI Insights chat</span>
        <button type="submit" disabled={!message.trim()} aria-label="Open AI Insights and send message">
          <SendHorizontal size={18} />
        </button>
      </div>
    </form>
  );
}

export default function LandingABTab({ aiInsightsEnabled, onAskInsights, onOpenTests }) {
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectLogInUsername);
  const tests = useSelector(selectTests) || [];
  const metricsByTest = useSelector(selectTestMetrics) || {};
  const [loading, setLoading] = useState(true);
  const cfUsername = sessionUser?.split("-prod@")[0] || "";

  useEffect(() => {
    let active = true;
    if (!cfUsername) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    Promise.allSettled([
      dispatch(fetchTests({ cf_username: cfUsername, forceRefresh: true })),
      dispatch(fetchAllMetricsapi({ cf_username: cfUsername, forceRefresh: true })),
    ]).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [cfUsername, dispatch]);

  const validTests = useMemo(
    () => tests.filter((test) => test?.basicInfo && test?._id !== "store_info"),
    [tests],
  );
  const topTest = validTests.find((test) => String(test?.metadata?.status).toLowerCase() === "active") || validTests[0];
  const topMetrics = metricsByTest[testId(topTest)] || [];
  const summary = summarizeMetrics(topMetrics);
  const dailySeries = buildDailySeries(topMetrics);
  const liftLabel = summary.lift === null ? "No measured lift" : `${summary.lift >= 0 ? "+" : ""}${summary.lift.toFixed(1)}%`;
  const hasMeasuredLift = summary.lift !== null;
  const activeRows = validTests.slice(0, 5).map((test) => ({
    id: testId(test),
    name: testName(test),
    ...statusPresentation(test, metricsByTest[testId(test)] || []),
  }));
  const topAction = topTest
    ? hasMeasuredLift
      ? `${summary.lift >= 0 ? "Validate and consider rolling out the variant" : "Keep the control and review the variant"} for ${testName(topTest)}.`
      : `Keep ${testName(topTest)} running until it has enough traffic for a reliable decision.`
    : "Start a focused experiment on the highest-traffic landing page.";

  if (loading) {
    return <div className="cf-home-preview-skeleton" aria-label="Loading Landing A/B overview"><span /><span /><span /><span /></div>;
  }

  return (
    <section className="cf-home-agent-preview landing-ab" aria-label="Landing A/B overview">
      <div className="cf-home-preview-status">
        <span><i className={validTests.length ? "" : "muted"} /> {validTests.length ? `${validTests.length} experiment${validTests.length === 1 ? "" : "s"} connected` : "Experiment workspace ready"}</span>
        <button type="button" onClick={onOpenTests}>Open A/B testing <ArrowUpRight size={15} /></button>
      </div>

      <div className="cf-home-preview-grid">
        <button type="button" className="cf-home-performance-card cf-home-ab-card" onClick={onOpenTests}>
          <div className="cf-home-card-heading">
            <span>Conversion lift{topTest ? `, ${testName(topTest)}` : ""}</span>
            <small>Last {DAYS_TO_SHOW} days</small>
          </div>
          <strong className={`cf-home-big-number ${hasMeasuredLift && summary.lift < 0 ? "negative" : ""}`}>{liftLabel}</strong>
          <span className="cf-home-trend neutral">
            {topMetrics.length ? `Variant ${summary.variant.rate.toFixed(1)}% vs control ${summary.control.rate.toFixed(1)}%` : "Launch or sync an experiment to measure conversion lift"}
          </span>
          <div className="cf-home-ab-legend"><span><i /> Control</span><span><i /> Variant</span></div>
          <div className="cf-home-ab-chart">
            <svg viewBox="0 0 620 138" role="img" aria-label="Control and variant conversion rate trend">
              <line x1="12" y1="124" x2="608" y2="124" />
              <polyline className="control" points={chartPoints(dailySeries, "control")} />
              <polyline className="variant" points={chartPoints(dailySeries, "variant")} />
            </svg>
            <div>{dailySeries.map((day) => <span key={day.label}>{day.label}</span>)}</div>
          </div>
          <div className="cf-home-metric-pills">
            <span>{validTests.length} tests tracked</span>
            <span>{summary.conversionLabel === "purchase" ? "Purchase" : "Click"} conversion</span>
          </div>
        </button>

        <div className="cf-home-action-stack">
          <button type="button" className="cf-home-top-action" onClick={onOpenTests}>
            <span>Top action</span>
            <strong>{topAction}</strong>
            <small>Evidence first / human controlled</small>
          </button>
          <button type="button" className="cf-home-secondary-action" onClick={onOpenTests}>
            <span>Also worth doing</span>
            <strong>{validTests.length ? "Review every active test for sample size and statistical confidence before rollout." : "Test the primary CTA before redesigning the full landing page."}</strong>
          </button>
        </div>
      </div>

      <button type="button" className="cf-home-ab-tests" onClick={onOpenTests}>
        <span className="cf-home-ab-tests-title">Active tests</span>
        {activeRows.length ? activeRows.map((test) => (
          <span className="cf-home-ab-test-row" key={test.id}>
            <i className={test.tone} />
            <strong>{test.name}</strong>
            <em className={test.tone}>{test.text}</em>
          </span>
        )) : (
          <span className="cf-home-ab-empty">No experiments found. Open A/B testing to create the first controlled test.</span>
        )}
      </button>

      {aiInsightsEnabled && <InsightsCommand onSubmit={onAskInsights} />}
    </section>
  );
}
